import { Entity } from '@backstage/catalog-model';
import { RybbitFilter } from './types';

/**
 * The Rybbit site to read. Usually the numeric site id; the tracker's
 * `data-site-id` may be a different identifier on the same site. Passed
 * through unvalidated — whether a given form works is the API's answer to give,
 * and it surfaces as the panel's error.
 */
export const RYBBIT_SITE_ID_ANNOTATION = 'rybbit.io/site-id';

/** Narrows a shared site to one host. Comma-separated for several. */
export const RYBBIT_HOSTNAME_ANNOTATION = 'rybbit.io/hostname';

/** Narrows a shared site to a path prefix. Comma-separated for several. */
export const RYBBIT_PATH_PREFIX_ANNOTATION = 'rybbit.io/path-prefix';

export interface RybbitEntityScope {
  siteId: string;
  /** Empty when the entity is the whole site. */
  filters: RybbitFilter[];
}

function list(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

/**
 * Resolves an entity to the Rybbit site it reports on, and to the filters that
 * narrow a shared site down to it.
 *
 * Rybbit's `data-tag` is a script-side label with no filter dimension behind it,
 * so hostname and pathname are what scoping has to be expressed in.
 */
export function buildScopeFilters(
  hostname: string | undefined,
  pathPrefix: string | undefined,
): RybbitFilter[] {
  const filters: RybbitFilter[] = [];
  const hostnames = list(hostname);
  if (hostnames.length) {
    filters.push({ parameter: 'hostname', type: 'equals', value: hostnames });
  }
  const prefixes = list(pathPrefix);
  if (prefixes.length) {
    filters.push({ parameter: 'pathname', type: 'contains', value: prefixes });
  }
  return filters;
}

export function readRybbitAnnotations(
  entity: Entity,
): RybbitEntityScope | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const siteId = annotations[RYBBIT_SITE_ID_ANNOTATION]?.trim();
  if (!siteId) return undefined;
  return {
    siteId,
    filters: buildScopeFilters(
      annotations[RYBBIT_HOSTNAME_ANNOTATION],
      annotations[RYBBIT_PATH_PREFIX_ANNOTATION],
    ),
  };
}

/** True when the entity carries enough to render the tab and the card. */
export function isRybbitAvailable(entity: Entity): boolean {
  return readRybbitAnnotations(entity) !== undefined;
}
